// frontend/src/samkalp-faculty/SamkalpFacultyLoginPage.js
//
// Samkalp-branded faculty login. Performs the exact steps
// pages/ClassroomLoginPage.js already does (POST /tenant/{slug}/classroom/token,
// store access_token, dispatch setUser with app_context:'classroom'), then
// calls onLogin() so the parent SamkalpFaculty gate re-renders straight
// into SamkalpFacultyDashboard — mirrors SamkalpAdminLoginPage's onLogin
// prop exactly. Deliberately does NOT navigate() to /tenant/{slug}/copilot:
// that's the generic, tenant-agnostic Teaching Copilot route, and sending
// faculty there bypasses the dedicated Samkalp dashboard shell entirely
// (the bug this replaced — new faculty had no way back into their own
// dashboard after login).
import React, { useState } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import api from '../api/api';
import { setUser } from '../redux/slices/userSlice';
import { SAMKALP_CENTRES } from '../samkalp-admin/samkalpCentres';

const NAVY = '#092957';
const GOLD = '#f1a51c';

const SamkalpFacultyLoginPage = ({ onLogin }) => {
    const [centreSlug, setCentreSlug] = useState(SAMKALP_CENTRES[0]?.slug || '');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const dispatch = useDispatch();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!acceptedTerms) {
            setError('You must accept the Terms of Service and Privacy Policy to continue.');
            return;
        }
        setLoading(true); setError('');
        try {
            const res = await api.post(
                `/tenant/${centreSlug}/classroom/token?terms_accepted=true`,
                new URLSearchParams({ username: email, password })
            );
            localStorage.setItem('access_token', res.data.access_token);
            dispatch(setUser({ email, tenant_slug: centreSlug, app_context: 'classroom' }));
            onLogin();
        } catch (err) {
            setError(err.response?.data?.detail || 'Sign in failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${NAVY} 0%, #061f43 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 20, padding: '40px 36px', boxShadow: '0 24px 64px rgba(0,0,0,0.22)' }}>
                <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <div style={{ fontSize: 28, color: GOLD }}>☀</div>
                    <h3 style={{ fontWeight: 800, color: NAVY, margin: '8px 0 2px' }}>SAMKALP</h3>
                    <p style={{ color: '#72809b', fontSize: '0.85rem', margin: 0 }}>Faculty Portal</p>
                </div>

                {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-uppercase text-muted">Centre</Form.Label>
                        <Form.Select value={centreSlug} onChange={e => setCentreSlug(e.target.value)} required>
                            {SAMKALP_CENTRES.map(c => (
                                <option key={c.slug} value={c.slug}>{c.label}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-uppercase text-muted">Email</Form.Label>
                        <Form.Control type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-uppercase text-muted">Password</Form.Label>
                        <Form.Control type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                    </Form.Group>
                    <Form.Group className="mb-4">
                        <Form.Check
                            type="checkbox"
                            id="samkalp-faculty-terms"
                            checked={acceptedTerms}
                            onChange={e => setAcceptedTerms(e.target.checked)}
                            label={<span className="small text-muted">I agree to the Terms of Service and Privacy Policy.</span>}
                        />
                    </Form.Group>
                    <Button type="submit" disabled={loading || !acceptedTerms} style={{ width: '100%', background: NAVY, border: 'none', borderRadius: 10, padding: '12px', fontWeight: 700 }}>
                        {loading ? <Spinner animation="border" size="sm" /> : 'Enter Faculty Portal'}
                    </Button>
                </Form>
            </div>
        </div>
    );
};

export default SamkalpFacultyLoginPage;
